import { inject, Injectable } from '@angular/core';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { WINDOW } from '@df/dom/globals';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LocalCustomerModel } from '../../customer/customer.model.decorator';
import { AntavoService } from '../antavo/antavo.service';
import { LocalGtmService } from '../gtm/gtm.service.decorator';
import { UserNotificationsService } from '../user-notifications/user-notifications.service';

declare module '@df/core/service/df-event' {
    interface IEventType {
        enableAntavoPush: DfEventConfig<'enableAntavoPush', undefined, void>;
    }
}
declare global {
    interface Window {
        ReactNativeWebView?: {
            postMessage?: (message: string) => void;
        };
    }
}

declare module '@df/dom/globals' {
    namespace NDfDomGlobals {
        interface Window {
            ReactNativeWebView?: {
                postMessage?: (message: string) => void;
            };
        }
    }
}

@Injectable({ providedIn: 'root' })
export class PwaNativeBridgeService {
    private app = inject(App);
    private window = inject(WINDOW);
    private customerModel = inject(LocalCustomerModel);
    private antavoService = inject(AntavoService);
    private dfEventService = inject(DfEventService);
    private userNotificationsService = inject(UserNotificationsService);
    private gtmService = inject(LocalGtmService);
    private subscriptions = new Subscription();

    // Emits null initially, then true/false when RN reports permission status
    readonly pushPermissionAllowed$ = new BehaviorSubject<boolean>(false);

    constructor() {
        if (this.app.isPlatformBrowser) {
            this.subscriptions.add(this.customerModel.obs.status.subscribe(isLoggedIn => this.appNotificationsUpdateIdentity(isLoggedIn)));

            this.subscriptions.add(this.customerModel.obs.data.subscribe(() => this.enableAntavoPush()));

            // Listen for RN -> WebView push permission messages
            this.window.addEventListener?.('message', this.handleMessage as EventListener);

            if (this.app.isPlatformBrowser && this.window?.ReactNativeWebView?.postMessage) {
                this.window.ReactNativeWebView.postMessage('checkPushPermission');
            }
        }
    }

    protected ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.window.removeEventListener?.('message', this.handleMessage as EventListener);
    }

    allowNotification(): void {
        if (this.app.isPlatformBrowser && this.window?.ReactNativeWebView?.postMessage) {
            this.window.ReactNativeWebView.postMessage('allowNotification');
        }
    }

    openNotificationSettings(): void {
        if (this.app.isPlatformBrowser && this.window?.ReactNativeWebView?.postMessage) {
            this.window.ReactNativeWebView.postMessage('openNotificationSettings');
        }
    }

    private readonly handleMessage = (event: MessageEvent): void => {
        try {
            const data = event.data;
            if (data?.type === 'pushPermission' && data.allowed !== this.pushPermissionAllowed$.value) {
                this.pushPermissionAllowed$.next(!!data.allowed);
                if (!!data.allowed) {
                    this.enableAntavoPush();
                }
            } else if (data?.type === 'appResumed') {
                this.handleAppResumed();
            } else if (data?.type === 'appleTrackingStatus') {
                this.handleAppleTrackingStatus(data.allowed);
            }
        } catch {
            // ignore malformed messages
        }
    };

    private handleAppleTrackingStatus(allowed: boolean): void {
        const eventData = {
            event: 'appleTrackingStatus',
            isGranted: allowed ? 1 : 0
        };
        this.gtmService.push(eventData);
    }

    private appNotificationsUpdateIdentity(isLoggedIn: boolean): void {
        if (this.app.isPlatformBrowser && this.window?.ReactNativeWebView?.postMessage) {
            if (isLoggedIn) {
                const email = this.customerModel.data?.email;
                this.window.ReactNativeWebView.postMessage(`email:${email}`);
            } else {
                this.window.ReactNativeWebView.postMessage('deidentified');
            }
        }
    }

    private async enableAntavoPush() {
        const loyaltyId = this.customerModel.loyaltyId;
        if (
            loyaltyId &&
            this.pushPermissionAllowed$.value &&
            !this.customerModel.antavoCustomerData?.value?.properties.push_notif_awarded
        ) {
            await this.antavoService.sendCustomEvent(
                {
                    loyaltyId: loyaltyId,
                    action: 'enable_push'
                },
                new Status()
            );
            this.dfEventService.dispatch({
                id: 'enableAntavoPush',
                data: undefined
            });
        }
    }

    private async handleAppResumed(): Promise<void> {
        if (this.customerModel.isLoggedIn && this.customerModel.data) {
            // Refresh Antavo member status
            await this.customerModel.refreshAntavoMemberStatus(this.customerModel.data);

            // Refresh notifications
            await this.userNotificationsService.refreshNotifications();
        }
    }

    postMessage(text: string) {
        if (this.app.isPlatformBrowser && this.window?.ReactNativeWebView?.postMessage) {
            this.window.ReactNativeWebView.postMessage(text);
        }
    }
}
